import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useMutation, useQuery} from "@apollo/client";
import {LatLong, MarkerSpot, PolygonSpot, Spot, SpotType, WalkLine} from "../types";
import {getWalkLinesQuery, saveWalkLinesMutation} from "./graphQl";
import {generateWalkLines, reconcileWalkLines} from "./generateWalkLines";
import {buildSpotRuleGraph} from "../../pages/areaDetails/customFlow/spotRuleGraph";
import {AppContext} from "../context/ContextWrapper";
import {
    EditableWalkLine,
    EditableWalkLinePoint,
    ResolvedWalkLine,
    WalkLineEditCallbacks,
} from "../map/AdvancedMap";

export type UseWalkLinesResult = {
    resolvedWalkLines: ResolvedWalkLine[];
    editableWalkLines: EditableWalkLine[] | undefined;
    walkLineEditCallbacks: WalkLineEditCallbacks;
    walkLineStartSpotIds: Set<string>;
    activeLines: WalkLine[];
    handleGenerateWalkLines: () => void;
    handleClearWalkLines: () => void;
};

export const useWalkLines = (
    areaId: string | undefined,
    spots: (MarkerSpot | PolygonSpot)[],
    canEdit: boolean,
): UseWalkLinesResult => {
    const appContext = useContext(AppContext);
    const {data: walkLinesData} = useQuery(getWalkLinesQuery, {
        variables: {areaId},
        skip: !areaId,
    });

    const [saveWalkLinesMut] = useMutation(saveWalkLinesMutation);
    const [localWalkLines, setLocalWalkLines] = useState<WalkLine[] | null>(null);
    const lastServerLinesRef = useRef<string | null>(null);

    // Initialize / re-sync local walk lines from server data
    const serverLines = walkLinesData?.getWalkLines?.lines;
    const serverLinesSignature = serverLines ? JSON.stringify(serverLines) : null;
    if (serverLines && serverLinesSignature !== lastServerLinesRef.current) {
        lastServerLinesRef.current = serverLinesSignature;
        setLocalWalkLines(serverLines.map((line: any) => ({
            id: line.id,
            points: (line.points || []).map((p: any) => ({
                ...(p.spotKey ? {spotKey: p.spotKey} : {}),
                ...(typeof p.lat === "number" ? {lat: p.lat} : {}),
                ...(typeof p.lng === "number" ? {lng: p.lng} : {}),
            })),
        })));
    }

    const activeLines = useMemo(() => localWalkLines ?? [], [localWalkLines]);

    // Build spot position lookup by key
    const spotPositionByKey = useMemo(() => {
        const map = new Map<string, LatLong>();
        spots.forEach((spot: Spot) => {
            const key = spot.key || spot.id;
            if (spot.type === SpotType.Marker) {
                map.set(key, (spot as MarkerSpot).position);
            } else if (spot.type === SpotType.Polygon) {
                const coords = (spot as PolygonSpot).coords || [];
                if (coords.length > 0) {
                    map.set(key, {
                        lat: coords.reduce((sum, c) => sum + c.lat, 0) / coords.length,
                        lng: coords.reduce((sum, c) => sum + c.lng, 0) / coords.length,
                    });
                }
            }
        });
        return map;
    }, [spots]);

    // Persist walk lines to server
    const persistWalkLines = useCallback((lines: WalkLine[]) => {
        if (!areaId) return;
        saveWalkLinesMut({
            variables: {
                areaId,
                lines: lines.map((line) => ({
                    id: line.id,
                    points: line.points.map((p) => ({
                        ...(p.spotKey ? {spotKey: p.spotKey} : {}),
                        ...(typeof p.lat === "number" ? {lat: p.lat} : {}),
                        ...(typeof p.lng === "number" ? {lng: p.lng} : {}),
                    })),
                })),
            },
        }).then(() => {
            appContext.notifyContentChanged();
        });
    }, [areaId, saveWalkLinesMut, appContext]);

    // Reconcile walk lines when spots or rules change
    const spotRuleSignature = useMemo(() =>
        spots.map((s) => {
            const rules = (s.triggerRules?.rules || []).map((r) => `${r.spotKey || ""}:${r.eventType}`).join(",");
            return `${s.id}|${s.key}|${rules}`;
        }).sort().join(";"),
    [spots]);
    const prevSpotRuleSignatureRef = useRef(spotRuleSignature);

    useEffect(() => {
        if (prevSpotRuleSignatureRef.current === spotRuleSignature) return;
        prevSpotRuleSignatureRef.current = spotRuleSignature;

        if (!localWalkLines || localWalkLines.length === 0) return;

        const reconciled = reconcileWalkLines(localWalkLines, spots);
        setLocalWalkLines(reconciled);
        persistWalkLines(reconciled);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to spotRuleSignature changes
    }, [spotRuleSignature]);

    // Compute start spot keys from the rule graph
    const walkLineStartSpotIds: Set<string> = useMemo(() => {
        if (activeLines.length === 0) return new Set();
        const graph = buildSpotRuleGraph(spots);
        const startKeys = new Set<string>();
        for (const nodeId of graph.startNodeIds) {
            const spot = graph.spotByNodeId.get(nodeId);
            if (spot) startKeys.add(spot.key || spot.id);
        }
        return startKeys;
    }, [spots, activeLines.length]);

    // Read-only resolved walk lines (non-edit mode)
    const resolvedWalkLines: ResolvedWalkLine[] = useMemo(() => {
        if (canEdit) return [];
        if (activeLines.length === 0 || spots.length === 0) return [];

        return activeLines.map((line) => {
            const firstSpotKey = line.points.find((p) => p.spotKey)?.spotKey;
            return {
                id: line.id,
                isStart: !!firstSpotKey && walkLineStartSpotIds.has(firstSpotKey),
                points: (line.points || [])
                    .map((point) => {
                        if (point.spotKey) {
                            const pos = spotPositionByKey.get(point.spotKey);
                            return pos ? {lat: pos.lat, lng: pos.lng} : undefined;
                        }
                        if (typeof point.lat === "number" && typeof point.lng === "number") {
                            return {lat: point.lat, lng: point.lng};
                        }
                        return undefined;
                    })
                    .filter((p): p is LatLong => !!p),
            };
        }).filter((line) => line.points.length >= 2);
    }, [canEdit, activeLines, spots, spotPositionByKey, walkLineStartSpotIds]);

    // Editable walk lines with resolved positions (edit mode only)
    const editableWalkLines: EditableWalkLine[] | undefined = useMemo(() => {
        if (!canEdit || activeLines.length === 0) return undefined;

        const lines = activeLines.map((line) => ({
            id: line.id,
            points: (line.points || [])
                .map((point): EditableWalkLinePoint | undefined => {
                    if (point.spotKey) {
                        const pos = spotPositionByKey.get(point.spotKey);
                        return pos ? {spotKey: point.spotKey, lat: pos.lat, lng: pos.lng, isSpotAnchor: true} : undefined;
                    }
                    if (typeof point.lat === "number" && typeof point.lng === "number") {
                        return {lat: point.lat, lng: point.lng, isSpotAnchor: false};
                    }
                    return undefined;
                })
                .filter((p): p is EditableWalkLinePoint => !!p),
        })).filter((line) => line.points.length >= 2);

        return lines.length > 0 ? lines : undefined;
    }, [canEdit, activeLines, spotPositionByKey]);

    // Walk line edit callbacks
    const walkLineEditCallbacks: WalkLineEditCallbacks = useMemo(() => ({
        onInsertWaypoint: (lineId: string, afterIndex: number, position: LatLong) => {
            setLocalWalkLines((prev) => {
                const lines = (prev ?? []).map((line) => {
                    if (line.id !== lineId) return line;
                    const newPoints = [...line.points];
                    newPoints.splice(afterIndex + 1, 0, {lat: position.lat, lng: position.lng});
                    return {...line, points: newPoints};
                });
                persistWalkLines(lines);
                return lines;
            });
        },
        onMoveWaypoint: (lineId: string, pointIndex: number, position: LatLong) => {
            setLocalWalkLines((prev) => {
                const lines = (prev ?? []).map((line) => {
                    if (line.id !== lineId) return line;
                    const newPoints = [...line.points];
                    const existing = newPoints[pointIndex];
                    if (existing?.spotKey) return line;
                    newPoints[pointIndex] = {lat: position.lat, lng: position.lng};
                    return {...line, points: newPoints};
                });
                persistWalkLines(lines);
                return lines;
            });
        },
        onDeleteWaypoint: (lineId: string, pointIndex: number) => {
            setLocalWalkLines((prev) => {
                const lines = (prev ?? []).map((line) => {
                    if (line.id !== lineId) return line;
                    const point = line.points[pointIndex];
                    if (point?.spotKey) return line;
                    const newPoints = line.points.filter((_, i) => i !== pointIndex);
                    return {...line, points: newPoints};
                }).filter((line) => line.points.length >= 2);
                persistWalkLines(lines);
                return lines;
            });
        },
    }), [persistWalkLines]);

    const handleGenerateWalkLines = useCallback(() => {
        const generated = generateWalkLines(spots);
        setLocalWalkLines(generated);
        persistWalkLines(generated);
    }, [spots, persistWalkLines]);

    const handleClearWalkLines = useCallback(() => {
        setLocalWalkLines([]);
        persistWalkLines([]);
    }, [persistWalkLines]);

    return {
        resolvedWalkLines,
        editableWalkLines,
        walkLineEditCallbacks,
        walkLineStartSpotIds,
        activeLines,
        handleGenerateWalkLines,
        handleClearWalkLines,
    };
};
